import React, { useState, useEffect } from 'react';
import Header from '../components/Layout/Header';
import { auditAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ClipboardList, Filter } from 'lucide-react';
import { toast } from 'sonner';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchLogs();
  }, [filterType]);

  const fetchLogs = async () => {
    try {
      const params = filterType !== 'all' ? { entity_type: filterType } : {};
      const response = await auditAPI.getLogs(params);
      setLogs(response.data);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      leave_approve: 'bg-emerald-100 text-emerald-800',
      leave_reject: 'bg-red-100 text-red-800',
      salary_update: 'bg-blue-100 text-blue-800',
      employee_create: 'bg-purple-100 text-purple-800',
      employee_update: 'bg-amber-100 text-amber-800',
    };
    return colors[action] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div data-testid="audit-logs-page">
      <Header title="Audit Logs" subtitle="Track all system activities and changes" />

      <div className="p-8">
        {/* Filter */}
        <div className="flex items-center gap-4 mb-6">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-48" data-testid="filter-type">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="leave_application">Leave Applications</SelectItem>
              <SelectItem value="salary_record">Salary Records</SelectItem>
              <SelectItem value="employee">Employees</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Logs Timeline */}
        <div className="dashboard-card">
          {logs.length === 0 ? (
            <div className="empty-state py-12">
              <ClipboardList className="w-12 h-12 text-slate-300" />
              <h3>No audit logs found</h3>
              <p>System activities will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {logs.map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg"
                  data-testid={`audit-log-${log.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                    <ClipboardList className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getActionBadge(log.action)}`}>
                        {log.action.replace(/_/g, ' ').toUpperCase()}
                      </span>
                      <span className="text-xs text-slate-500">
                        {log.entity_type}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 mb-1">
                      {log.details ? JSON.stringify(log.details) : 'No additional details'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(log.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;
